import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

const TIERS = [
  {
    name: "Spotlight Gold",
    tag: "The Entrance",
    features: ["Standard directory placement", "Verified identity badge", "Up to 6 photographs", "City-level search priority"],
  },
  {
    name: "Platinum Luxe",
    tag: "The Statement",
    features: ["Priority directory placement", "Verified badge + review access", "Up to 15 photographs & video", "Homepage rotation eligibility"],
    featured: true,
  },
  {
    name: "Diamond Elite",
    tag: "The Inner Circle",
    features: ["Top-tier featured placement", "Dedicated profile concierge", "Unlimited gallery & travel calendar", "Multi-city visibility"],
  },
];

export default function MembershipTiers() {
  return (
    <section id="membership" data-testid="membership-section" className="border-t hairline-gold bg-onyx/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 lg:py-32">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-xs uppercase tracking-[0.35em] text-gold/80 mb-4">Provider Advertising Memberships</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-amber-100/95">
            Be seen by the <span className="italic gold-text">right eyes</span>
          </h2>
          <p className="mt-4 text-sm text-neutral-500">Founding member pricing extended on application — payment processing in final negotiation.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.name}
              data-testid={`tier-card-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative p-8 lg:p-10 border transition-all duration-500 card-glow ${
                t.featured ? "bg-elevated border-amber-500/40" : "bg-onyx hairline-gold"
              }`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
            >
              {t.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gold text-obsidian text-[10px] uppercase tracking-[0.25em] font-medium">
                  Most Coveted
                </span>
              )}
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold/70 mb-2">{t.tag}</p>
              <h3 className="font-serif text-2xl lg:text-3xl font-light text-amber-100/95 mb-2">{t.name}</h3>
              <p className="text-sm text-neutral-500 mb-8">Pricing on application</p>
              <ul className="space-y-3 mb-10">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-neutral-300 font-light">
                    <Check size={14} className="text-gold mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/join/provider"
                data-testid={`tier-apply-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`block text-center px-6 py-3.5 text-xs uppercase tracking-[0.2em] transition-all duration-300 ${
                  t.featured
                    ? "bg-gold text-obsidian font-medium hover:bg-gold-bright"
                    : "border hairline-gold text-amber-100/90 hover:border-amber-400/60 hover:bg-amber-500/5"
                }`}
              >
                Apply Now
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
